import { useEffect, useRef } from 'react';

interface Props {
  /** Nulls the form-submit ref this form registered. Called once, on unmount. */
  clear: () => void;
}

/**
 * Clears a form's registered submit handler when that form unmounts.
 *
 * The Formik render props on the three credential forms assign
 * `submitRef.current = handleSubmit` *during render* and never clear it, so
 * leaving edit mode (or swapping views) would otherwise leave the
 * unsaved-changes bar holding a handler belonging to a form that is no longer
 * mounted. Render this inside `<Formik>` — next to `FormikDirtyReporter` —
 * where its lifetime is exactly the form's: mounting it clears nothing (so it
 * can never race the render-time assignment), and its cleanup runs precisely
 * when the form goes away.
 */
export const SubmitRefCleanup = ({ clear }: Props) => {
  // `clear` is an inline arrow at every call site; keeping it in a ref means
  // the cleanup is registered once, on mount, rather than re-running whenever
  // the identity changes.
  const clearRef = useRef(clear);
  clearRef.current = clear;
  useEffect(() => () => clearRef.current(), []);
  return null;
};
